import { Device } from "mediasoup-client";
import RadioMixer from "./RadioMixer";

const ICE_SERVERS_DEFAULT = [
  { urls: "stun:pluglr.com:5349" },
  {
    urls: "turn:pluglr.com:5349?transport=udp",
    username: "mypluggers",
    credential: "plug12keys345",
  },
  {
    urls: "turn:pluglr.com:5349?transport=tcp",
    username: "mypluggers",
    credential: "plug12keys345",
  },
];

const BASE_AUDIO_CONSTRAINTS = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
};

const noop = () => {};
const STREAM_TYPE_RADIO_MIX = "radio-mix";
const STREAM_TYPE_RADIO_CALL = "radio-call";
const STREAM_TYPE_RADIO_COHOST = "radio-cohost";
const MEDIA_KIND_AUDIO = "audio";

function normalizeProducer(candidate) {
  if (!candidate) return null;
  if (typeof candidate === "string") {
    return {
      producerId: candidate,
      kind: null,
      type: null,
      peerId: null,
      id: null,
    };
  }

  const producerId = candidate.producerId || candidate.id;
  if (!producerId) return null;
  const raw = candidate.raw || candidate;
  const appData = raw?.appData || candidate.appData || null;

  return {
    producerId,
    kind: candidate.kind || candidate.producerKind || null,
    type: candidate.type || candidate.streamType || appData?.type || null,
    peerId:
      candidate.peerId ||
      candidate.callerId ||
      candidate.socketId ||
      appData?.callerId ||
      appData?.peerId ||
      null,
    id: candidate.id || null,
    callerSocketId: candidate.callerSocketId || appData?.callerSocketId || null,
    appData,
    raw,
  };
}

function ensureError(err) {
  if (!err) return new Error("Unknown mediasoup error");
  if (err instanceof Error) return err;
  if (typeof err === "string") return new Error(err);
  if (typeof err === "object") {
    let serialized = "";
    try {
      serialized = JSON.stringify(err);
    } catch (jsonErr) {
      serialized = String(err);
    }

    const message =
      err.message ||
      err.msg ||
      err.error ||
      err.reason ||
      err.description ||
      err.details?.message ||
      err.data?.message ||
      err.statusText ||
      err.code ||
      err.status ||
      err.statusCode ||
      (serialized ? `Unknown mediasoup error ${serialized}` : null);

    const normalized = new Error(
      message || "Unknown mediasoup error (empty server response)"
    );
    if (err.code || err.status || err.statusCode) {
      normalized.code = err.code || err.status || err.statusCode;
    }
    if (err.details) {
      normalized.details = err.details;
    }
    if (err.data) {
      normalized.data = err.data;
    } else if (!normalized.details) {
      normalized.data = err;
    }
    normalized.original = err;
    return normalized;
  }
  return new Error(String(err));
}

function emitWithAck(socket, event, payload = {}) {
  return new Promise((resolve, reject) => {
    if (!socket || typeof socket.emit !== "function") {
      reject(new Error("Socket not connected"));
      return;
    }
    socket.emit(event, payload, (error, response) => {
      if (error) {
        reject(ensureError(error));
        return;
      }
      if (response && response.error) {
        reject(ensureError(response.error));
        return;
      }
      resolve(response);
    });
  });
}

export class WebRTCHostClient {
  constructor({
    socket,
    channelId,
    eventId,
    user,
    iceServers = ICE_SERVERS_DEFAULT,
    onStatusChange = noop,
    onError = noop,
    onIncomingCall = noop,
    onCallerStateChange = noop,
    onActiveCallersChange = noop,
    onCohostStreamsChange = noop,
    cohosts = [],
  }) {
    this.socket = socket;
    this.channelId = channelId;
    this.eventId = eventId;
    this.user = user || {};
    this.cohosts = cohosts;
    this.iceServers = iceServers;
    this.onStatusChange = onStatusChange;
    this.onError = onError;
    this.onIncomingCall = onIncomingCall;
    this.onCallerStateChange = onCallerStateChange;
    this.onActiveCallersChange = onActiveCallersChange;
    this.onCohostStreamsChange = onCohostStreamsChange;

    this.device = null;
    this.sendTransport = null;
    this.recvTransport = null;
    this.mixer = null;
    this.monitoringEnabled = true;
    this.micAudienceMuted = false;
    this.micAudienceVolume = 1;
    this.micMonitorVolume = 1;
    this.micMonitorEnabled = true;
    this.musicAudienceMuted = false;
    this.musicAudienceVolume = 0.6;
    this.musicMonitorVolume = 0.6;
    this.musicMonitorEnabled = true;
    this.duckingEnabled = true;
    this.duckingReducedGain = 0.15;
    this.monitorDuckingReducedGain = 0.3;
    this.micStream = null;
    this.radioProducer = null;
    this.fileSourceInfo = null;
    this.musicPlayback = {
      buffer: null,
      fileInfo: null,
      loop: false,
      offset: 0,
      startedAt: null,
      isPlaying: false,
      duration: 0,
      ignoreEnded: false,
    };
    this.musicPlaybackEntry = null;
    this.onMusicEnded = null;
    this.connected = false;
    this.audioDeviceId = null;

    this._sendTransportParams = null;
    this._recvTransportParams = null;

    this.pendingCallRequests = new Map();
    this.remoteCallers = new Map();
    this.remoteConsumers = new Map();
    this.cohostMuteStates = new Map();

    this._registerSocketHandlers();
    this._handleMusicEnded = this._handleMusicEnded.bind(this);
  }

  _buildProducerAppData(extra = {}) {
    const base = {
      type: "radio-mix",
    };

    return {
      ...base,
      ...extra,
    };
  }

  _setStatus(message) {
    try {
      this.onStatusChange(message);
    } catch (err) {
      console.warn("WebRTCHostClient status handler error", err);
    }
  }

  _reportError(error) {
    try {
      this.onError(error instanceof Error ? error : new Error(String(error)));
    } catch (err) {
      console.warn("WebRTCHostClient error handler error", err);
    }
  }

  setAudioInputDevice(deviceId) {
    const nextId = deviceId || null;
    if (this.audioDeviceId === nextId) {
      return;
    }
    this.audioDeviceId = nextId;
    if (this.micStream) {
      this.startMicrophone().catch((err) => {
        this._reportError(err);
      });
    }
  }

  _registerSocketHandlers() {
    if (!this.socket) return;

    this.socket.on("disconnect", () => {
      this.connected = false;
      this._setStatus("Disconnected");
    });

    this.socket.on("connect", () => {
      this.connected = true;
      this._setStatus("Connected");
    });

    this.socket.on("transport-recreate-needed", async ({ direction }) => {
      try {
        await this.recreateTransport(direction);
      } catch (err) {
        this._reportError(err);
      }
    });

    this.socket.on("workerRestarted", async () => {
      try {
        await this.prepare(true);
        if (this.radioProducer) {
          await this._maybeReadyToBroadcast();
        }
      } catch (err) {
        this._reportError(err);
      }
    });

    this.socket.on("callRequest", async (payload) => {
      try {
        await this._handleCallRequest(payload);
      } catch (err) {
        this._reportError(err);
      }
    });

    this.socket.on("newProducer", async (payload) => {
      try {
        await this._handleNewProducer(payload);
      } catch (err) {
        this._reportError(err);
      }
    });

    this.socket.on("producerClosed", ({ producerId }) => {
      this._handleProducerClosed(producerId);
    });

    this.socket.on("caller-left", ({ callerId, producerId }) => {
      this._handleCallerLeft({ callerId, producerId });
    });
  }

  async _handleCallRequest(payload = {}) {
    const {
      requesterSocketId,
      requesterPeerId,
      requesterName,
      notifiedSocketIds,
    } = payload;

    if (!requesterSocketId) {
      return;
    }

    const callerName = requesterName || "Listener";
    const requestedAt = Date.now();

    const entry = {
      requesterSocketId,
      requesterPeerId,
      requesterName: callerName,
      notifiedSocketIds: Array.isArray(notifiedSocketIds)
        ? notifiedSocketIds
        : [],
      requestedAt,
    };

    const existingPeer = this.remoteCallers.get(requesterSocketId) || {};
    this.remoteCallers.set(requesterSocketId, {
      producerId: existingPeer.producerId || null,
      name: callerName,
      peerId: requesterSocketId,
      id: requesterPeerId,
      kind: "audio",
      consumed: existingPeer.consumed || false,
      type: STREAM_TYPE_RADIO_CALL,
    });

    this.pendingCallRequests.set(requesterSocketId, entry);

    this._notifyIncomingCall({
      ...entry,
      accept: () => this.acceptCaller(requesterSocketId),
      reject: () => this.rejectCaller(requesterSocketId),
    });
  }

  _notifyIncomingCall(request) {
    try {
      this.onIncomingCall(request);
    } catch (err) {
      console.warn("HostClient incoming call handler error", err);
    }
  }

  _notifyCallerState(event) {
    try {
      this.onCallerStateChange(event);
    } catch (err) {
      console.warn("HostClient caller state handler error", err);
    }
  }

  _notifyActiveCallers() {
    if (typeof this.onActiveCallersChange !== "function") {
      return;
    }

    const activeCallers = [];
    for (const [callerId, entry] of this.remoteCallers.entries()) {
      if (!entry || !entry.consumed) continue;
      activeCallers.push({
        callerId,
        name: entry.name || "Caller",
        peerId: entry.peerId || entry.id || null,
        producerId: entry.producerId || null,
        streamActive: Boolean(entry.stream),
      });
    }

    try {
      this.onActiveCallersChange(activeCallers);
    } catch (err) {
      console.warn("HostClient active callers handler error", err);
    }
  }

  _normalizePeerId(peerId) {
    if (peerId === undefined || peerId === null) {
      return null;
    }
    return String(peerId);
  }

  _normalizeCohostKey({ userId, email, fallback } = {}) {
    if (userId !== undefined && userId !== null && userId !== "") {
      return String(userId);
    }
    if (typeof email === "string" && email.trim()) {
      return `email:${email.trim().toLowerCase()}`;
    }
    if (fallback !== undefined && fallback !== null) {
      return String(fallback);
    }
    return null;
  }

  _findCohostRecordByKey(key) {
    if (!key) return null;
    if (!Array.isArray(this.cohosts)) return null;
    const normalizedKey = String(key);
    const isEmailKey = normalizedKey.startsWith("email:");
    const emailTarget = isEmailKey
      ? normalizedKey.slice(6)
      : normalizedKey.toLowerCase();
    return (
      this.cohosts.find((cohost) => {
        const candidateId =
          cohost?.id || cohost?._id || cohost?.userId || cohost?.uid;
        if (candidateId && String(candidateId) === normalizedKey) {
          return true;
        }
        if (isEmailKey && typeof cohost?.email === "string") {
          return cohost.email.trim().toLowerCase() === emailTarget;
        }
        return false;
      }) || null
    );
  }

  _applyStoredCohostMuteState(entry) {
    if (!entry || entry.type !== STREAM_TYPE_RADIO_COHOST) {
      return false;
    }

    if (!this.mixer || !entry.sourceId) {
      return false;
    }

    const key = entry.cohostKey || this._normalizeCohostKey({
      userId: entry.peerId,
      email: entry.cohostEmail,
    });
    if (!key) return false;

    const shouldMute = Boolean(this.cohostMuteStates.get(key));
    this.mixer.setSourceAudienceEnabled(entry.sourceId, !shouldMute);
    return true;
  }

  _notifyCohostStreams() {
    if (typeof this.onCohostStreamsChange !== "function") {
      return;
    }

    const payload = [];
    for (const entry of this.remoteConsumers.values()) {
      if (entry?.type !== STREAM_TYPE_RADIO_COHOST) continue;
      const cohostKey =
        entry.cohostKey ||
        this._normalizeCohostKey({
          userId: entry.peerId,
          email: entry.cohostEmail,
          fallback: entry.sourceId,
        });
      const cohostRecord = this._findCohostRecordByKey(cohostKey);
      payload.push({
        peerId: cohostKey,
        producerId: entry.producerId || null,
        sourceId: entry.sourceId || null,
        muted: Boolean(this.cohostMuteStates.get(cohostKey)),
        isLive: true,
        name:
          cohostRecord?.fname || cohostRecord?.lname
            ? `${cohostRecord?.fname || ""} ${cohostRecord?.lname || ""}`.trim()
            : cohostRecord?.name || null,
        email: cohostRecord?.email || entry.cohostEmail || null,
      });
    }

    try {
      this.onCohostStreamsChange(payload);
    } catch (err) {
      console.warn("HostClient cohost stream handler error", err);
    }
  }

  setCohostMuted(peerId, muted = true) {
    const normalizedPeerId = this._normalizeCohostKey({
      userId: peerId,
      email: typeof peerId === "string" && peerId.startsWith("email:")
        ? peerId.slice(6)
        : null,
      fallback: peerId,
    });
    if (!normalizedPeerId) {
      return false;
    }

    this.cohostMuteStates.set(normalizedPeerId, Boolean(muted));
    let applied = false;
    for (const entry of this.remoteConsumers.values()) {
      if (
        entry?.type === STREAM_TYPE_RADIO_COHOST &&
        (entry.cohostKey ||
          this._normalizeCohostKey({
            userId: entry.peerId,
            email: entry.cohostEmail,
          })) === normalizedPeerId
      ) {
        applied = this._applyStoredCohostMuteState(entry) || applied;
      }
    }
    this._notifyCohostStreams();
    return applied;
  }

  toggleCohostMute(peerId) {
    const normalized = this._normalizeCohostKey({
      userId: peerId,
      email:
        typeof peerId === "string" && peerId.startsWith("email:")
          ? peerId.slice(6)
          : null,
      fallback: peerId,
    });
    if (!normalized) {
      return false;
    }
    const current = Boolean(this.cohostMuteStates.get(normalized));
    return this.setCohostMuted(normalized, !current);
  }

  async addCallersToProgram(callerIds) {
    if (!Array.isArray(callerIds) || callerIds.length === 0) {
      return;
    }

    if (
      !this.sendTransport ||
      this.sendTransport.connectionState !== "connected"
    ) {
      throw new Error("Broadcast not ready to accept callers");
    }

    if (!this.socket) {
      throw new Error("Socket not connected");
    }

    const callerEntries = callerIds
      .map((callerId) => {
        if (!callerId) return null;
        const request = this.pendingCallRequests.get(callerId);
        const remotePeer = this.remoteCallers.get(callerId);
        return {
          callerId,
          requesterPeerId: request?.requesterPeerId || remotePeer?.id || null,
          requesterSocketId: callerId,
          requesterName: request?.requesterName || remotePeer?.name || null,
        };
      })
      .filter(Boolean);

    const uniqueCallerIds = [
      ...new Set(callerEntries.map((entry) => entry.callerId)),
    ];
    if (!uniqueCallerIds.length) {
      return;
    }

    const payload = {
      callerIds: uniqueCallerIds,
      roomId: this.eventId,
      channelId: this.channelId,
      entries: callerEntries,
    };

    await new Promise((resolve, reject) => {
      this.socket.emit("addCallers", payload, (error, response) => {
        if (error) {
          reject(ensureError(error));
          return;
        }
        if (response && response.error) {
          reject(ensureError(response.error));
          return;
        }
        resolve(response);
      });
    })
      .then((response) => {
        uniqueCallerIds.forEach((callerId) => {
          this.pendingCallRequests.delete(callerId);
          this._notifyCallerState({ callerId, status: "accepted", response });
        });
      })
      .catch((err) => {
        uniqueCallerIds.forEach((callerId) => {
          this._notifyCallerState({
            callerId,
            status: "error",
            message: err.message,
          });
        });
        throw err;
      })
      .then(() => this._refreshRemoteCallers())
      .catch((err) => {
        // _refreshRemoteCallers already logged errors internally; only rethrow original ones
        if (err instanceof Error) {
          throw err;
        }
      });
  }

  acceptCaller(callerSocketId) {
    if (!callerSocketId) {
      return Promise.reject(new Error("Missing caller identifier"));
    }
    return this.addCallersToProgram([callerSocketId]);
  }

  rejectCaller(callerSocketId, { reason } = {}) {
    if (!callerSocketId) {
      return Promise.resolve();
    }

    this.pendingCallRequests.delete(callerSocketId);
    this.remoteCallers.delete(callerSocketId);

    this._notifyCallerState({
      callerId: callerSocketId,
      status: "rejected",
      message: reason,
    });

    if (this.socket && typeof this.socket.emit === "function") {
      try {
        this.socket.emit(
          "rejectCallers",
          { callerIds: [callerSocketId], reason },
          noop
        );
      } catch (err) {
        console.warn("rejectCaller emit failed", err);
      }
    }

    this._notifyActiveCallers();

    return Promise.resolve();
  }

  endCaller(callerSocketId, { reason } = {}) {
    if (!callerSocketId) {
      return Promise.resolve();
    }

    const normalizedReason = reason || "Call ended by host";
    const relatedConsumers = Array.from(this.remoteConsumers.entries()).filter(
      ([, entry]) => entry.callerSocketId === callerSocketId
    );

    relatedConsumers.forEach(([consumerId]) => {
      this._cleanupRemoteConsumer(consumerId, "host-ended");
    });

    this.pendingCallRequests.delete(callerSocketId);
    this.remoteCallers.delete(callerSocketId);

    this._notifyCallerState({
      callerId: callerSocketId,
      status: "ended",
      message: normalizedReason,
    });
    this._notifyActiveCallers();

    if (this.socket && typeof this.socket.emit === "function") {
      return new Promise((resolve) => {
        try {
          this.socket.emit(
            "rejectCallers",
            { callerIds: [callerSocketId], reason: normalizedReason },
            () => resolve()
          );
        } catch (err) {
          console.warn("endCaller emit failed", err);
          resolve();
        }
      });
    }

    return Promise.resolve();
  }

  async prepare(force = false) {
    if (!this.socket) {
      throw new Error("Socket not connected");
    }

    if (!force && this.device && this.sendTransport) {
      return;
    }

    await this.joinRoom();
    await this._loadDevice();
    await this._ensureTransports();
  }

  async joinRoom() {
    const cohostIds = this.cohosts.map((c) => c.id || c._id).filter(Boolean);

    await emitWithAck(this.socket, "joinRoom", {
      roomId: this.eventId,
      isHost: true,
      hostId: this.user?.id,
      email: this.user?.email,
      channelId: this.channelId,
      role: "host",
      cohosts: cohostIds,
    });
    this._setStatus("Joined room");
  }

  async _loadDevice() {
    if (this.device) return;

    const { rtpCapabilities } = await emitWithAck(
      this.socket,
      "getRouterRtpCapabilities",
      { roomId: this.eventId }
    );

    if (!rtpCapabilities) {
      throw new Error("Router RTP capabilities missing");
    }

    const device = new Device();
    await device.load({ routerRtpCapabilities: rtpCapabilities });
    this.device = device;
    this._setStatus("Mediasoup device ready");
  }

  async _ensureTransports() {
    if (this.sendTransport && this.recvTransport) return;

    const transportResponse = await emitWithAck(
      this.socket,
      "createTransport",
      {
        forceTcp: false,
        producing: true,
        consuming: true,
        roomId: this.eventId,
        channelId: this.channelId,
      }
    );

    const { sendTransportParams, recvTransportParams } =
      transportResponse || {};
    if (!sendTransportParams) {
      throw new Error("Missing send transport parameters");
    }

    this._sendTransportParams = sendTransportParams;
    this._recvTransportParams = recvTransportParams || null;

    this.sendTransport = this.device.createSendTransport({
      ...sendTransportParams,
      iceServers: this.iceServers,
    });

    this._wireSendTransport(this.sendTransport, sendTransportParams.id);

    if (recvTransportParams) {
      this.recvTransport = this.device.createRecvTransport({
        ...recvTransportParams,
        iceServers: this.iceServers,
      });
      this._wireRecvTransport(this.recvTransport, recvTransportParams.id);
    }
  }

  _wireSendTransport(transport, serverTransportId) {
    transport.on("connect", ({ dtlsParameters }, callback, errback) => {
      emitWithAck(this.socket, "connectTransport", {
        transportId: serverTransportId,
        dtlsParameters,
        roomId: this.eventId,
        channelId: this.channelId,
      })
        .then(() => callback())
        .catch((err) => errback(err));
    });

    transport.on(
      "produce",
      ({ kind, rtpParameters, appData }, callback, errback) => {
        const payloadAppData = this._buildProducerAppData(appData || {});
        const userId = this.user?.id ?? null;
        console.log("Producing media for user", userId);
        emitWithAck(this.socket, "produce", {
          transportId: serverTransportId,
          kind,
          rtpParameters,
          appData: payloadAppData,
          // roomId: this.eventId,
          // channelId: this.channelId,
        })
          .then((response) => {
            if (process.env.NODE_ENV !== "production") {
              try {
                const normalized =
                  response === undefined
                    ? "<undefined>"
                    : typeof response === "string"
                    ? response
                    : JSON.stringify(response, null, 2);
                console.debug(
                  "produce ack response:\n",
                  normalized,
                  "\nrequest payload:\n",
                  JSON.stringify(
                    {
                      transportId: serverTransportId,
                      kind,
                      hasRtpParameters: Boolean(rtpParameters),
                      appData: payloadAppData,
                      // roomId: this.eventId,
                      // channelId: this.channelId,
                    },
                    null,
                    2
                  )
                );
              } catch (logErr) {
                console.warn("produce ack log failed", logErr);
              }
            }
            const producerId =
              (response &&
                typeof response === "object" &&
                (response.id || response.producerId)) ||
              (typeof response === "string" ? response : null);
            if (!producerId) {
              throw new Error("Producer id missing in server response");
            }
            callback({ id: producerId });
            this._maybeReadyToBroadcast();
          })
          .catch((err) => errback(err));
      }
    );

    transport.on("connectionstatechange", (state) => {
      this._setStatus(`Send transport: ${state}`);
      if (state === "failed") {
        this.recreateTransport("send").catch(this._reportError.bind(this));
      }
    });
  }

  _wireRecvTransport(transport, serverTransportId) {
    transport.on("connect", ({ dtlsParameters }, callback, errback) => {
      emitWithAck(this.socket, "connectTransport", {
        transportId: serverTransportId,
        dtlsParameters,
        roomId: this.eventId,
        channelId: this.channelId,
      })
        .then(() => callback())
        .catch((err) => errback(err));
    });

    transport.on("connectionstatechange", (state) => {
      this._setStatus(`Recv transport: ${state}`);
      if (state === "failed") {
        this.recreateTransport("recv").catch(this._reportError.bind(this));
      }
    });
  }

  async recreateTransport(direction) {
    if (direction !== "send" && direction !== "recv") return;

    const transportResponse = await emitWithAck(
      this.socket,
      "createTransport",
      {
        forceTcp: false,
        producing: direction === "send",
        consuming: direction === "recv",
        roomId: this.eventId,
        channelId: this.channelId,
      }
    );

    if (direction === "send") {
      if (this.sendTransport) {
        this.sendTransport.close();
      }
      const params = transportResponse?.sendTransportParams;
      if (!params) throw new Error("Failed to recreate send transport");
      this._sendTransportParams = params;
      this.sendTransport = this.device.createSendTransport({
        ...params,
        iceServers: this.iceServers,
      });
      this._wireSendTransport(this.sendTransport, params.id);
      if (this.radioProducer && this.mixer) {
        const track = this.mixer.getTrack();
        if (track) {
          this.radioProducer = await this.sendTransport.produce({
            track,
            appData: this._buildProducerAppData(),
          });
        }
      }
    } else if (direction === "recv") {
      if (this.recvTransport) {
        this.recvTransport.close();
      }
      const params = transportResponse?.recvTransportParams;
      if (!params) throw new Error("Failed to recreate recv transport");
      this._recvTransportParams = params;
      this.recvTransport = this.device.createRecvTransport({
        ...params,
        iceServers: this.iceServers,
      });
      this._wireRecvTransport(this.recvTransport, params.id);
    }
  }

  async _ensureMixer() {
    if (!this.mixer) {
      const monitorEnabled =
        this.monitoringEnabled &&
        (this.micMonitorEnabled || this.musicMonitorEnabled);
      this.mixer = new RadioMixer({
        monitorEnabled,
        duckingEnabled: this.duckingEnabled,
        duckedTarget: ["file"],
        monitorSource: "mic",
        normalGain: 1,
        duckedGain: this.duckingReducedGain,
        monitorNormalGain: 1,
        monitorDuckedGain: this.monitorDuckingReducedGain,
      });
    }
    await this.mixer.resume();
    this._applyMixerState();
    return this.mixer;
  }

  async startMicrophone() {
    await this.prepare();
    await this._ensureMixer();

    if (this.micStream) {
      try {
        this.micStream.getTracks().forEach((track) => track.stop());
      } catch (err) {
        console.warn("startMicrophone stop previous tracks", err);
      }
      this.micStream = null;
      this.mixer.removeSource("mic");
    }

    const audioConstraints = { ...BASE_AUDIO_CONSTRAINTS };
    if (this.audioDeviceId) {
      audioConstraints.deviceId = { exact: this.audioDeviceId };
    }

    this.micStream = await navigator.mediaDevices.getUserMedia({
      audio: audioConstraints,
      video: false,
    });

    const [micTrack] = this.micStream.getAudioTracks();
    if (!micTrack) {
      throw new Error("No audio track available from microphone");
    }
    this.mixer.addTrack("mic", micTrack, {
      audienceGain: this.micAudienceVolume,
      monitorGain: this.micMonitorVolume,
      audienceEnabled: !this.micAudienceMuted,
      monitorEnabled: this.monitoringEnabled && this.micMonitorEnabled,
    });

    this._applyMicSettings();
    this._syncGlobalMonitorState();

    if (this.radioProducer) {
      const mixedTrack = this.mixer.getTrack();
      if (mixedTrack) {
        await this._replaceProducerTrack(mixedTrack);
      }
    }

    return this.micStream;
  }

  async startBroadcast() {
    await this.startMicrophone();

    const mixerTrack = this.mixer.getTrack();
    if (!mixerTrack) {
      throw new Error("Missing mixed audio track");
    }

    if (this.radioProducer) {
      return this.radioProducer;
    }

    this.radioProducer = await this.sendTransport.produce({
      track: mixerTrack,
      appData: this._buildProducerAppData(),
      codecOptions: {
        opusStereo: true,
        opusDtx: true,
        opusFec: true,
        opusPtime: 20,
      },
    });

    this.radioProducer.on("transportclose", () => {
      this.radioProducer = null;
    });

    this.radioProducer.on("trackended", () => {
      this.stopBroadcast();
    });

    this._setStatus("Broadcasting");
    await this._maybeReadyToBroadcast();
    return this.radioProducer;
  }

  async _replaceProducerTrack(track) {
    if (!this.radioProducer) return;
    try {
      await this.radioProducer.replaceTrack({ track });
      this._setStatus("Microphone source updated");
    } catch (err) {
      this._reportError(err);
    }
  }

  async _maybeReadyToBroadcast() {
    if (!this.socket) return;
    if (
      !this.sendTransport ||
      this.sendTransport.connectionState !== "connected"
    )
      return;
    if (
      this.recvTransport &&
      this.recvTransport.connectionState !== "connected"
    )
      return;
    emitWithAck(this.socket, "readyToBroadcast").catch((err) => {
      console.warn("readyToBroadcast failed", err);
    });
  }

  async loadMusicFile(file) {
    if (!file) return null;
    await this.startMicrophone();
    if (!this.mixer) {
      throw new Error("Mixer unavailable for music playback");
    }

    const arrayBuffer = await file.arrayBuffer();
    const audioBuffer = await this.mixer.audioContext.decodeAudioData(
      arrayBuffer
    );

    this.stopMusic({ resetPosition: true, clearBuffer: false });

    this.musicPlayback.buffer = audioBuffer;
    this.musicPlayback.duration = audioBuffer.duration || 0;
    this.musicPlayback.offset = 0;
    this.musicPlayback.loop = false;
    this.musicPlayback.startedAt = null;
    this.musicPlayback.isPlaying = false;
    this.musicPlayback.fileInfo = {
      name: file.name,
      size: file.size,
      type: file.type,
    };
    this.fileSourceInfo = this.musicPlayback.fileInfo;

    return {
      duration: this.musicPlayback.duration,
      fileInfo: this.musicPlayback.fileInfo,
    };
  }

  async playMusicFile(file, options = {}) {
    const { loop = false } = options;
    const info = await this.loadMusicFile(file);
    this._startMusicBuffer({ offset: 0, loop });
    this._setStatus(`Playing ${file.name}`);
    return info;
  }

  _startMusicBuffer({ offset = 0, loop = false } = {}) {
    if (!this.mixer || !this.musicPlayback.buffer) {
      return;
    }

    this._detachMusicSource();

    const entry = this.mixer.addBuffer("file", this.musicPlayback.buffer, {
      loop,
      offset,
      audienceGain: this.musicAudienceVolume,
      monitorGain: this.musicMonitorVolume,
      audienceEnabled: !this.musicAudienceMuted,
      monitorEnabled: this.monitoringEnabled && this.musicMonitorEnabled,
    });

    if (entry?.bufferSource) {
      entry.bufferSource.onended = this._handleMusicEnded;
    }

    this.musicPlaybackEntry = entry;
    this.musicPlayback.offset = offset;
    this.musicPlayback.startedAt = this.mixer.audioContext?.currentTime ?? null;
    if (this.musicPlayback.startedAt !== null) {
      this.musicPlayback.startedAt -= offset;
    }
    this.musicPlayback.loop = !!loop;
    this.musicPlayback.isPlaying = true;
    this.musicPlayback.ignoreEnded = false;

    this._applyMusicSettings();
    this._syncGlobalMonitorState();
  }

  _detachMusicSource() {
    if (!this.mixer || !this.musicPlaybackEntry) {
      return;
    }

    const bufferSource = this.musicPlaybackEntry.bufferSource;
    if (bufferSource) {
      bufferSource.onended = null;
    }

    this.musicPlayback.ignoreEnded = true;
    try {
      this.mixer.removeSource("file");
    } catch (err) {
      console.warn("Failed to remove music source", err);
    }
    this.musicPlayback.ignoreEnded = false;
    this.musicPlaybackEntry = null;
  }

  pauseMusic() {
    if (
      !this.mixer ||
      !this.musicPlayback.buffer ||
      !this.musicPlayback.isPlaying
    ) {
      return null;
    }

    const ctx = this.mixer.audioContext;
    if (ctx && this.musicPlayback.startedAt !== null) {
      const elapsed = Math.max(
        0,
        ctx.currentTime - this.musicPlayback.startedAt
      );
      this.musicPlayback.offset = Math.min(
        elapsed,
        this.musicPlayback.duration || elapsed
      );
    }

    this.musicPlayback.isPlaying = false;
    this.musicPlayback.startedAt = null;
    this._detachMusicSource();
    return this.musicPlayback.offset;
  }

  resumeMusic(options = {}) {
    if (!this.mixer || !this.musicPlayback.buffer) {
      return false;
    }

    const { loop = this.musicPlayback.loop } = options;
    this._startMusicBuffer({ offset: this.musicPlayback.offset, loop });
    return true;
  }

  stopMusic({ resetPosition = true, clearBuffer = false } = {}) {
    if (this.musicPlaybackEntry) {
      this._detachMusicSource();
    }

    this.musicPlayback.isPlaying = false;
    this.musicPlayback.startedAt = null;
    if (resetPosition) {
      this.musicPlayback.offset = 0;
    }

    if (clearBuffer) {
      this.musicPlayback.buffer = null;
      this.musicPlayback.duration = 0;
      this.musicPlayback.fileInfo = null;
      this.fileSourceInfo = null;
    }

    this._syncGlobalMonitorState();
  }

  seekMusic(positionSeconds, { autoPlay = false } = {}) {
    if (!this.mixer || !this.musicPlayback.buffer) {
      return false;
    }

    const duration = this.musicPlayback.duration || 0;
    const nextOffset = Math.max(
      0,
      Math.min(Number(positionSeconds) || 0, duration)
    );
    const wasPlaying = this.musicPlayback.isPlaying;

    this.musicPlayback.offset = nextOffset;

    if (wasPlaying || autoPlay) {
      this._startMusicBuffer({
        offset: nextOffset,
        loop: this.musicPlayback.loop,
      });
    }

    return true;
  }

  setMusicLoop(loop) {
    const next = !!loop;
    this.musicPlayback.loop = next;
    if (this.musicPlaybackEntry?.bufferSource) {
      this.musicPlaybackEntry.bufferSource.loop = next;
    }
  }

  getMusicPlaybackState() {
    const hasBuffer = !!this.musicPlayback.buffer;
    const duration = this.musicPlayback.duration || 0;
    let position = this.musicPlayback.offset || 0;

    if (hasBuffer && this.musicPlayback.isPlaying && this.mixer?.audioContext) {
      const ctx = this.mixer.audioContext;
      if (this.musicPlayback.startedAt !== null) {
        position = Math.max(0, ctx.currentTime - this.musicPlayback.startedAt);
        if (!this.musicPlayback.loop) {
          position = Math.min(position, duration);
        }
      }
    }

    return {
      isLoaded: hasBuffer,
      isPlaying: this.musicPlayback.isPlaying,
      position,
      duration,
      loop: this.musicPlayback.loop,
      fileInfo: this.musicPlayback.fileInfo,
    };
  }

  setMusicEndedCallback(callback) {
    this.onMusicEnded = typeof callback === "function" ? callback : null;
  }

  _handleMusicEnded() {
    if (this.musicPlayback.ignoreEnded) {
      return;
    }

    this.musicPlayback.isPlaying = false;
    this.musicPlayback.startedAt = null;
    if (!this.musicPlayback.loop) {
      this.musicPlayback.offset = this.musicPlayback.duration || 0;
    }

    if (typeof this.onMusicEnded === "function") {
      try {
        this.onMusicEnded();
      } catch (err) {
        console.warn("Music ended callback failed", err);
      }
    }
  }

  async playAudioFile(file, options = {}) {
    const { loop = true } = options;
    return this.playMusicFile(file, { loop });
  }

  stopAudioFile() {
    this.stopMusic({ resetPosition: true, clearBuffer: true });
  }

  setMicMuted(muted) {
    this.micAudienceMuted = !!muted;
    if (!this.mixer) return;
    this.mixer.setSourceAudienceEnabled("mic", !this.micAudienceMuted);
  }

  setFileMuted(muted) {
    this.musicAudienceMuted = !!muted;
    if (!this.mixer) return;
    this.mixer.setSourceAudienceEnabled("file", !this.musicAudienceMuted);
  }

  setMicAudienceVolume(value) {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return;
    this.micAudienceVolume = numeric;
    if (this.mixer) {
      this.mixer.setSourceAudienceGain("mic", numeric);
    }
  }

  setMicMonitorVolume(value) {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return;
    this.micMonitorVolume = numeric;
    if (this.mixer) {
      this.mixer.setSourceMonitorGain("mic", numeric);
    }
  }

  setMusicAudienceVolume(value) {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return;
    this.musicAudienceVolume = numeric;
    if (this.mixer) {
      this.mixer.setSourceAudienceGain("file", numeric);
    }
  }

  setMusicMonitorVolume(value) {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return;
    this.musicMonitorVolume = numeric;
    if (this.mixer) {
      this.mixer.setSourceMonitorGain("file", numeric);
    }
  }

  setMicMonitorEnabled(enabled) {
    this.micMonitorEnabled = !!enabled;
    if (this.mixer) {
      this.mixer.setSourceMonitorEnabled(
        "mic",
        this.monitoringEnabled && this.micMonitorEnabled
      );
      this._syncGlobalMonitorState();
    }
  }

  setMusicMonitorEnabled(enabled) {
    this.musicMonitorEnabled = !!enabled;
    if (this.mixer) {
      this.mixer.setSourceMonitorEnabled(
        "file",
        this.monitoringEnabled && this.musicMonitorEnabled
      );
      this._syncGlobalMonitorState();
    }
  }

  setMonitoringEnabled(enabled) {
    this.monitoringEnabled = !!enabled;
    if (this.mixer) {
      this._applyMicSettings();
      this._applyMusicSettings();
      this._syncGlobalMonitorState();
    }
  }

  setDuckingEnabled(enabled) {
    this.duckingEnabled = !!enabled;
    if (this.mixer) {
      this.mixer.setDuckingEnabled(this.duckingEnabled);
    }
  }

  setDuckingStrength(value, { monitorResidual } = {}) {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      this.duckingReducedGain = Math.max(0, Math.min(numeric, 1));
    }

    if (Number.isFinite(monitorResidual)) {
      this.monitorDuckingReducedGain = Math.max(
        0,
        Math.min(monitorResidual, 1)
      );
    } else if (!Number.isFinite(this.monitorDuckingReducedGain)) {
      this.monitorDuckingReducedGain = Math.min(
        1,
        Math.max(this.duckingReducedGain + 0.2, 0.25)
      );
    }

    if (this.mixer) {
      this.mixer.configureDucking({
        duckedGain: this.duckingReducedGain,
        monitorDuckedGain: this.monitorDuckingReducedGain,
      });
    }
  }

  configureDucking(options = {}) {
    if (!options || typeof options !== "object") {
      return;
    }
    if (Number.isFinite(options.duckedGain)) {
      this.duckingReducedGain = Math.max(0, Math.min(options.duckedGain, 1));
    }
    if (Number.isFinite(options.monitorDuckedGain)) {
      this.monitorDuckingReducedGain = Math.max(
        0,
        Math.min(options.monitorDuckedGain, 1)
      );
    }
    if (this.mixer) {
      this.mixer.configureDucking({
        duckedGain: this.duckingReducedGain,
        monitorDuckedGain: this.monitorDuckingReducedGain,
      });
    }
  }

  _applyMixerState() {
    if (!this.mixer) return;
    this.mixer.setDuckingEnabled(this.duckingEnabled);
    this.mixer.configureDucking({
      duckedGain: this.duckingReducedGain,
      monitorDuckedGain: this.monitorDuckingReducedGain,
    });
    this._applyMicSettings();
    this._applyMusicSettings();
    this._syncGlobalMonitorState();
  }

  _applyMicSettings() {
    if (!this.mixer) return;
    this.mixer.setSourceAudienceGain("mic", this.micAudienceVolume);
    this.mixer.setSourceAudienceEnabled("mic", !this.micAudienceMuted);
    this.mixer.setSourceMonitorGain("mic", this.micMonitorVolume);
    this.mixer.setSourceMonitorEnabled(
      "mic",
      this.monitoringEnabled && this.micMonitorEnabled
    );
  }

  _applyMusicSettings() {
    if (!this.mixer) return;
    this.mixer.setSourceAudienceGain("file", this.musicAudienceVolume);
    this.mixer.setSourceAudienceEnabled("file", !this.musicAudienceMuted);
    this.mixer.setSourceMonitorGain("file", this.musicMonitorVolume);
    this.mixer.setSourceMonitorEnabled(
      "file",
      this.monitoringEnabled && this.musicMonitorEnabled
    );
  }

  _syncGlobalMonitorState() {
    if (!this.mixer) return;
    const hasRemoteCallers = this.remoteConsumers?.size > 0;
    const shouldMonitor =
      hasRemoteCallers ||
      (this.monitoringEnabled &&
        (this.micMonitorEnabled || this.musicMonitorEnabled));
    if (shouldMonitor) {
      this.mixer.startLocalMonitor();
    } else {
      this.mixer.stopLocalMonitor();
    }
  }

  async stopBroadcast() {
    this._clearRemoteConsumers();
    if (this.radioProducer) {
      try {
        this.radioProducer.close();
      } catch (err) {
        console.warn("stopBroadcast producer close", err);
      }
      this.radioProducer = null;
    }

    if (this.micStream) {
      this.micStream.getTracks().forEach((track) => track.stop());
      this.micStream = null;
    }

    if (this.mixer) {
      this.mixer.removeSource("mic");
      this.stopAudioFile();
    }

    this._setStatus("Broadcast stopped");
  }

  async cleanup() {
    try {
      await this.stopBroadcast();
    } catch (err) {
      console.warn("cleanup stopBroadcast", err);
    }

    if (this.mixer) {
      await this.mixer.close();
      this.mixer = null;
    }

    if (this.sendTransport) {
      try {
        this.sendTransport.close();
      } catch (err) {
        console.warn("cleanup sendTransport", err);
      }
      this.sendTransport = null;
    }

    if (this.recvTransport) {
      try {
        this.recvTransport.close();
      } catch (err) {
        console.warn("cleanup recvTransport", err);
      }
      this.recvTransport = null;
    }

    this.device = null;
    this._setStatus("Cleaned up");
  }

  async _handleNewProducer(payload = {}) {
    const normalized = normalizeProducer(payload);
    if (!normalized?.producerId) return;
    if (
      normalized.producerId &&
      this.radioProducer &&
      normalized.producerId === this.radioProducer.id
    ) {
      return;
    }

    const callerLink = this._findRemoteCallerByPeer(normalized.peerId);
    try {
      await this._consumeRemoteProducer(normalized, callerLink);
    } catch (err) {
      console.warn("HostClient consume remote producer failed", err);
    }
  }

  _handleProducerClosed(producerId) {
    if (!producerId) return;
    for (const [consumerId, entry] of this.remoteConsumers.entries()) {
      if (entry.producerId === producerId) {
        this._cleanupRemoteConsumer(consumerId, "producer-closed");
      }
    }
  }

  _handleCallerLeft({ callerId, producerId } = {}) {
    if (callerId && this.remoteCallers.has(callerId)) {
      this.remoteCallers.delete(callerId);
      for (const [consumerId, entry] of this.remoteConsumers.entries()) {
        if (entry.callerSocketId === callerId) {
          this._cleanupRemoteConsumer(consumerId, "caller-left");
        }
      }
      this._notifyCallerState({
        callerId,
        status: "ended",
        message: "Caller left",
      });
      this._notifyActiveCallers();
    }
    if (producerId) {
      this._handleProducerClosed(producerId);
    }
  }

  async _consumeRemoteProducer(producerInfo, cachedCallerLink = null) {
    if (!producerInfo?.producerId) {
      return null;
    }
    const { producerId, peerId, type } = producerInfo;
    if (type && type === STREAM_TYPE_RADIO_MIX) {
      return null;
    }

    for (const entry of this.remoteConsumers.values()) {
      if (entry.producerId === producerId) {
        return entry.consumer;
      }
    }

    const callerLink =
      cachedCallerLink ||
      this._findRemoteCallerByPeer(peerId || producerInfo.callerSocketId);

    await this._ensureRecvTransportReady();
    if (!this.recvTransport || !this.device) {
      console.warn(
        "HostClient: recv transport unavailable for remote consumption"
      );
      return null;
    }

    const consumePayload = {
      roomId: this.eventId,
      producerId,
      rtpCapabilities: this.device.rtpCapabilities,
      hostId: this.user?.id || this.socket?.id || null,
      channelId: this.channelId,
      peerId: peerId || null,
    };

    const consumeResponse = await emitWithAck(
      this.socket,
      "consume",
      consumePayload
    );
    const params = consumeResponse?.consumerParams || consumeResponse;
    const { id, kind, rtpParameters, type: responseType } = params || {};

    const normalizedKind = kind || MEDIA_KIND_AUDIO;
    if (normalizedKind !== MEDIA_KIND_AUDIO) {
      await emitWithAck(this.socket, "resume", { consumerId: id }).catch(
        (err) => {
          console.warn("HostClient resume non-audio consumer failed", err);
        }
      );
      return null;
    }

    const consumer = await this.recvTransport.consume({
      id,
      producerId,
      kind: normalizedKind,
      rtpParameters,
    });

    const callerSocketId =
      callerLink?.key || producerInfo.callerSocketId || peerId || null;

    const resolvedType =
      type || responseType || (callerLink ? STREAM_TYPE_RADIO_CALL : null);
    const isCohostStream = resolvedType === STREAM_TYPE_RADIO_COHOST;
    const cohostAppData =
      producerInfo?.appData || producerInfo?.raw?.appData || {};
    const appDataPeerId =
      cohostAppData.cohostUserId || cohostAppData.peerId || null;
    const cohostEmail = cohostAppData.cohostEmail || cohostAppData.email || null;
    const normalizedPeerId = this._normalizePeerId(
      appDataPeerId || peerId || callerLink?.entry?.id || null
    );
    const cohostKey = this._normalizeCohostKey({
      userId: normalizedPeerId,
      email: cohostEmail,
      fallback: callerSocketId || producerId,
    });

    const entry = {
      consumer,
      producerId,
      peerId: normalizedPeerId,
      callerSocketId,
      type: resolvedType,
      sourceId: null,
      cohostKey,
      cohostEmail,
      cohostUserId: normalizedPeerId,
    };

    this.remoteConsumers.set(consumer.id, entry);

    consumer.on("transportclose", () => {
      this._cleanupRemoteConsumer(consumer.id, "transport-closed");
    });
    consumer.on("producerclose", () => {
      this._cleanupRemoteConsumer(consumer.id, "producer-closed");
    });

    if (callerSocketId && !isCohostStream) {
      const baseEntry = callerLink?.entry || {
        producerId: null,
        name: "Caller",
        peerId: callerSocketId,
        id: peerId || callerSocketId,
        kind: "audio",
        type: STREAM_TYPE_RADIO_CALL,
      };
      const updated = {
        ...baseEntry,
        producerId,
        consumed: true,
        stream: new MediaStream([consumer.track]),
      };
      this.remoteCallers.set(callerSocketId, updated);
      this._notifyActiveCallers();
    }

    await emitWithAck(this.socket, "resumeConsumer", {
      consumerId: consumer.id,
      producerId,
    }).catch((err) => {
      console.warn("HostClient resumeConsumer failed", err);
    });

    try {
      await consumer.resume();
    } catch (err) {
      console.warn("HostClient local consumer resume failed", err);
    }

    await this._attachRemoteTrackToMixer(entry, consumer.track);
    if (isCohostStream) {
      this._notifyCohostStreams();
    }
    return consumer;
  }

  async _attachRemoteTrackToMixer(entry, track) {
    if (!track || track.readyState === "ended") {
      return;
    }
    await this._ensureMixer();
    if (!this.mixer) {
      console.warn("HostClient: mixer unavailable for remote track");
      return;
    }

    let sourceId;
    if (entry.type === STREAM_TYPE_RADIO_CALL && entry.peerId) {
      sourceId = `remote-${entry.peerId}`;
    } else if (entry.type === STREAM_TYPE_RADIO_COHOST && entry.peerId) {
      sourceId = `cohost-${entry.peerId}`;
    } else {
      sourceId = `remote-${entry.consumer.id}`;
    }

    // Hack: Attach to a hidden audio element to ensure the browser decodes the stream
    // even if the Web Audio API graph is complex or the tab is backgrounded.
    const audio = new Audio();
    audio.srcObject = new MediaStream([track]);
    audio.muted = true;
    audio.volume = 0;
    audio
      .play()
      .catch((err) =>
        console.warn("HostClient: remote audio element play failed", err)
      );
    entry.audioElement = audio;

    try {
      this.mixer.removeSource(sourceId);
    } catch (err) {
      // noop if source was missing
    }

    try {
      this.mixer.addTrack(sourceId, track, {
        audienceGain: 1,
        monitorGain: 1,
        audienceEnabled: true,
        monitorEnabled: true,
      });
      entry.sourceId = sourceId;
      if (entry.type === STREAM_TYPE_RADIO_COHOST) {
        this._applyStoredCohostMuteState(entry);
      }
      if (process.env.NODE_ENV !== "production") {
        console.debug(`HostClient: added remote source ${sourceId} to mixer`);
      }
      this._syncGlobalMonitorState();
    } catch (err) {
      console.warn("HostClient: failed to add remote track to mixer", err);
    }
  }

  _cleanupRemoteConsumer(consumerId, reason) {
    const entry = this.remoteConsumers.get(consumerId);
    if (!entry) return;
    this.remoteConsumers.delete(consumerId);

    if (entry.audioElement) {
      entry.audioElement.pause();
      entry.audioElement.srcObject = null;
      entry.audioElement = null;
    }

    if (entry.consumer) {
      try {
        entry.consumer.close();
      } catch (err) {
        console.warn("HostClient remote consumer close", err);
      }
    }

    if (entry.sourceId && this.mixer) {
      try {
        this.mixer.removeSource(entry.sourceId);
      } catch (err) {
        console.warn("HostClient remove remote source", err);
      }
    }

    if (entry.callerSocketId) {
      const caller = this.remoteCallers.get(entry.callerSocketId);
      if (caller) {
        this.remoteCallers.set(entry.callerSocketId, {
          ...caller,
          consumed: false,
          stream: null,
          producerId: null,
        });
        this._notifyActiveCallers();
      }
    }

    if (reason && process.env.NODE_ENV !== "production") {
      console.debug(`HostClient remote consumer cleaned (${reason})`);
    }

    this._syncGlobalMonitorState();
    if (entry.type === STREAM_TYPE_RADIO_COHOST) {
      this._notifyCohostStreams();
    }
  }

  async _refreshRemoteCallers() {
    try {
      const response = await emitWithAck(this.socket, "getProducers", {
        roomId: this.eventId,
      });
      const candidates = [];
      if (Array.isArray(response)) candidates.push(...response);
      if (Array.isArray(response?.producers))
        candidates.push(...response.producers);

      for (const candidate of candidates) {
        const normalized = normalizeProducer(candidate);
        if (!normalized?.producerId) continue;
        if (
          normalized.producerId &&
          this.radioProducer &&
          normalized.producerId === this.radioProducer.id
        ) {
          continue;
        }
        const callerLink = this._findRemoteCallerByPeer(normalized.peerId);
        await this._consumeRemoteProducer(normalized, callerLink);
      }
    } catch (err) {
      console.warn("HostClient getProducers failed", err);
    }
  }

  async _ensureRecvTransportReady() {
    if (!this.device) {
      await this._loadDevice();
    }
    if (this.recvTransport) {
      return this.recvTransport;
    }

    if (this._recvTransportParams?.id && this.device) {
      this.recvTransport = this.device.createRecvTransport({
        ...this._recvTransportParams,
        iceServers: this.iceServers,
      });
      this._wireRecvTransport(this.recvTransport, this._recvTransportParams.id);
      return this.recvTransport;
    }

    await this.recreateTransport("recv");
    return this.recvTransport;
  }

  _findRemoteCallerByPeer(peerId) {
    if (!peerId) return null;
    for (const [key, entry] of this.remoteCallers.entries()) {
      if (entry.peerId === peerId || entry.id === peerId || key === peerId) {
        return { key, entry };
      }
    }
    return null;
  }

  _clearRemoteConsumers() {
    for (const consumerId of Array.from(this.remoteConsumers.keys())) {
      this._cleanupRemoteConsumer(consumerId, "broadcast-stop");
    }
  }
}

export default WebRTCHostClient;
